import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Plus, Minus, ShoppingBag, MessageCircle, Check, X, ZoomIn, ArrowLeft, Heart } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { getTheme } from '../../lib/themes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CART_KEY = 'kj_cart_v1';
const FAVS_KEY = 'kj_favoritos_v1';

const loadCart = () => {
    try {
        const raw = localStorage.getItem(CART_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
};
const saveCart = (cart) => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch { /* ignore */ }
};

export const ProductoDetalle = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [producto, setProducto] = useState(null);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imgIdx, setImgIdx] = useState(0);
    const [zoom, setZoom] = useState(false);
    const [tipoCompra, setTipoCompra] = useState('pieza');
    const [tallaSel, setTallaSel] = useState('');
    const [cantidad, setCantidad] = useState(1);
    const [added, setAdded] = useState(false);
    const [cartCount, setCartCount] = useState(0);
    const [isFav, setIsFav] = useState(false);
    const [relacionados, setRelacionados] = useState([]);
    const [masDeMarca, setMasDeMarca] = useState([]);

    // Verificar si el producto esta en favoritos
    useEffect(() => {
        if (!producto?.id) return;
        try {
            const favs = JSON.parse(localStorage.getItem(FAVS_KEY) || '[]');
            setIsFav(favs.includes(producto.id));
        } catch { /* ignore */ }
    }, [producto?.id]);

    const toggleFavorito = () => {
        if (!producto?.id) return;
        try {
            const favs = JSON.parse(localStorage.getItem(FAVS_KEY) || '[]');
            const idx = favs.indexOf(producto.id);
            if (idx >= 0) {
                favs.splice(idx, 1);
                toast.success('Quitado de favoritos');
                setIsFav(false);
            } else {
                favs.push(producto.id);
                toast.success('❤️ Agregado a favoritos');
                setIsFav(true);
            }
            localStorage.setItem(FAVS_KEY, JSON.stringify(favs));
        } catch { /* ignore */ }
    };

    // Cargar relacionados y mas-de-marca cuando hay producto
    useEffect(() => {
        if (!producto?.id) return;
        (async () => {
            try {
                const [rel, mar] = await Promise.all([
                    axios.get(`${API_URL}/api/tienda/productos/${producto.id}/relacionados`).catch(() => ({ data: [] })),
                    axios.get(`${API_URL}/api/tienda/productos/${producto.id}/mas-de-marca`).catch(() => ({ data: [] }))
                ]);
                setRelacionados(rel.data || []);
                setMasDeMarca(mar.data || []);
            } catch (e) { /* ignore */ }
        })();
    }, [producto?.id]);

    useEffect(() => {
        setCartCount(loadCart().length);
        const handler = () => setCartCount(loadCart().length);
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
    }, []);

    const t = useMemo(() => {
        if (!config) return getTheme('denim_premium');
        return getTheme(config.tema_activo);
    }, [config]);

    useEffect(() => {
        const load = async () => {
            try {
                const [prod, cfg] = await Promise.all([
                    axios.get(`${API_URL}/api/tienda/productos/${id}`),
                    axios.get(`${API_URL}/api/tienda/config`)
                ]);
                setProducto(prod.data);
                setConfig(cfg.data);
                if (prod.data.tallas_disponibles?.length > 0) {
                    setTallaSel(prod.data.tallas_disponibles[0]);
                }
                // Registrar en "Vistos recientemente"
                try {
                    const RECIENT_KEY = 'kj_recientes_v1';
                    const raw = localStorage.getItem(RECIENT_KEY);
                    const recientes = raw ? JSON.parse(raw) : [];
                    // Remover si ya existia y agregar al principio
                    const filtered = recientes.filter(r => r.id !== prod.data.id);
                    filtered.unshift({
                        id: prod.data.id,
                        nombre: prod.data.nombre,
                        marca: prod.data.marca || '',
                        precio: prod.data.precio,
                        imagen: prod.data.imagenes?.[0] || '',
                        ts: Date.now()
                    });
                    // Limitar a 10
                    localStorage.setItem(RECIENT_KEY, JSON.stringify(filtered.slice(0, 10)));
                } catch (e) { /* ignore */ }
                // Scroll arriba al cargar nuevo producto (NO al volver atras)
                window.scrollTo({ top: 0, behavior: 'instant' });
            } catch (e) {
                toast.error('Producto no encontrado');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    const tienePaquete = (producto?.precio_paquete || 0) > 0;
    // Corrida disponible automaticamente cuando hay paquete (mitad de cantidad, +$10/pza)
    const tieneCorrida = tienePaquete || (producto?.precio_corrida || 0) > 0;

    // Flags globales de config: si el admin desactivo un tipo de venta, no se muestra
    const piezaActiva = config?.venta_pieza_activa !== false;
    const paqueteActiva = config?.venta_paquete_activa !== false;
    const corridaActiva = config?.venta_corrida_activa !== false;
    // Mostrar opcion solo si el producto la soporta Y el admin la habilito
    const showPieza = piezaActiva;
    const showPaquete = tienePaquete && paqueteActiva;
    const showCorrida = tieneCorrida && corridaActiva;

    // Si el tipoCompra actual queda oculto, cambiar a uno valido
    useEffect(() => {
        if (!config) return;
        if (tipoCompra === 'pieza' && !showPieza) {
            if (showPaquete) setTipoCompra('paquete');
            else if (showCorrida) setTipoCompra('corrida');
        } else if (tipoCompra === 'paquete' && !showPaquete) {
            if (showPieza) setTipoCompra('pieza');
            else if (showCorrida) setTipoCompra('corrida');
        } else if (tipoCompra === 'corrida' && !showCorrida) {
            if (showPieza) setTipoCompra('pieza');
            else if (showPaquete) setTipoCompra('paquete');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config, showPieza, showPaquete, showCorrida]);

    const precioActual = useMemo(() => {
        if (!producto) return 0;
        if (tipoCompra === 'paquete') return producto.precio_paquete || 0;
        if (tipoCompra === 'corrida') {
            // Si tiene precio_corrida explicito en BD, usarlo. Si no, calcular auto.
            if ((producto.precio_corrida || 0) > 0) return producto.precio_corrida;
            const tamano = producto.tamano_paquete || 14;
            const corridaPzs = Math.ceil(tamano / 2);
            const precioUnitarioPaq = (producto.precio_paquete || 0) / (tamano || 1);
            return Math.round((precioUnitarioPaq + 10) * corridaPzs * 100) / 100;
        }
        return producto.precio || 0;
    }, [tipoCompra, producto]);

    const labelTipo = (tc) => {
        const tamano = producto?.tamano_paquete || 14;
        const corridaPzs = Math.ceil(tamano / 2);
        return tc === 'paquete'
            ? `Paquete (${tamano} pzas)`
            : tc === 'corrida'
                ? `Corrida (${corridaPzs} pzas - mitad del paquete)`
                : 'Pieza individual';
    };

    const subtotal = precioActual * cantidad;

    const addToCart = () => {
        if (!producto) return;
        if (tipoCompra === 'pieza' && !tallaSel) { toast.error('Selecciona una talla'); return; }

        const item = {
            id: `${producto.id}-${tipoCompra}-${tallaSel || 'set'}-${Date.now()}`,
            producto_id: producto.id,
            nombre: producto.nombre,
            imagen: producto.imagenes?.[0] || '',
            talla: tipoCompra === 'pieza' ? tallaSel : (tipoCompra === 'paquete' ? `Paquete ${producto.tamano_paquete || 14}` : `Corrida ${Math.ceil((producto.tamano_paquete || 14) / 2)}`),
            color: producto.color || '',
            cantidad: cantidad,
            precio: precioActual,
            tipo_compra: tipoCompra
        };
        const cart = loadCart();
        cart.push(item);
        saveCart(cart);
        setCartCount(cart.length);
        setAdded(true);
        toast.success(`Agregado! Carrito: ${cart.length} producto${cart.length !== 1 ? 's' : ''}`);
        setTimeout(() => setAdded(false), 2000);
    };

    const cotizarWA = () => {
        const wa = (config?.whatsapp_numero || '').replace(/\D/g, '');
        if (!wa) { toast.error('WhatsApp no configurado'); return; }
        const url = `${window.location.origin}/producto/${producto.id}`;
        const msg = `Hola! Me interesa este producto:\n\n*${producto.nombre}*\nTipo: ${labelTipo(tipoCompra)}\nPrecio: $${precioActual.toLocaleString('es-MX')}\n\n${url}\n\n¿Me podrias dar mas informacion?`;
        window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, '_blank');
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center" style={{ background: t.colors.bg }}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>;
    }
    if (!producto) {
        return <div className="min-h-screen flex items-center justify-center" style={{ background: t.colors.bg, color: t.colors.text }}>
            <div className="text-center">
                <p className="mb-4">Producto no encontrado</p>
                <Link to="/tienda" className="text-amber-500 underline">Volver a la tienda</Link>
            </div>
        </div>;
    }

    const imagenes = producto.imagenes?.length ? producto.imagenes : [''];
    const etiquetas = producto.etiquetas || [];

    return (
        <div className="min-h-screen" style={{ background: t.colors.bg, color: t.colors.text }} data-testid="producto-detalle-page">
            {/* Header */}
            <header className="sticky top-0 z-30 backdrop-blur-xl border-b" style={{ background: t.colors.navBg, borderColor: t.colors.border }}>
                <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
                    <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm" style={{ color: t.colors.textMuted }} data-testid="btn-back">
                        <ArrowLeft className="w-4 h-4" /> Volver
                    </button>
                    <Link to="/tienda" className="flex items-center gap-2">
                        <img src="/logo-karry.png" alt="" className="w-7 h-7 rounded object-contain" />
                        <span className="text-base font-bold tracking-wider">KARRY JEAN'S</span>
                    </Link>
                    <Link to="/tienda?cart=1" className="relative p-2" data-testid="btn-cart-header">
                        <ShoppingBag className="w-5 h-5" />
                        {cartCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-bold"
                                style={{ background: t.colors.accent, color: t.colors.badgeText }}>
                                {cartCount}
                            </span>
                        )}
                    </Link>
                </div>
            </header>

            <div className="max-w-6xl mx-auto p-4 sm:p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Galeria */}
                    <div>
                        <div
                            className="relative aspect-square overflow-hidden rounded-2xl mb-3 cursor-zoom-in"
                            style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}` }}
                            onClick={() => imagenes[imgIdx] && setZoom(true)}
                            data-testid="galeria-principal"
                        >
                            {imagenes[imgIdx] ? (
                                <img src={imagenes[imgIdx]} alt={producto.nombre} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <ShoppingBag className="w-16 h-16" style={{ color: t.colors.border }} />
                                </div>
                            )}
                            {/* Etiquetas overlay */}
                            <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                                {etiquetas.includes('nuevo') && <span className="px-2 py-1 text-[11px] font-bold tracking-wider uppercase rounded" style={{ background: '#10B981', color: '#fff' }}>Nuevo</span>}
                                {etiquetas.includes('mas_vendido') && <span className="px-2 py-1 text-[11px] font-bold tracking-wider uppercase rounded" style={{ background: '#F59E0B', color: '#000' }}>Mas vendido</span>}
                                {etiquetas.includes('oferta') && <span className="px-2 py-1 text-[11px] font-bold tracking-wider uppercase rounded" style={{ background: '#EF4444', color: '#fff' }}>Oferta</span>}
                            </div>
                            {/* Nav fotos */}
                            {imagenes.length > 1 && (
                                <>
                                    <button onClick={(e) => { e.stopPropagation(); setImgIdx((imgIdx - 1 + imagenes.length) % imagenes.length); }}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70" data-testid="btn-img-prev">
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); setImgIdx((imgIdx + 1) % imagenes.length); }}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70" data-testid="btn-img-next">
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                    <div className="absolute bottom-3 right-3 px-2 py-1 rounded bg-black/60 text-white text-[10px] font-mono">{imgIdx + 1}/{imagenes.length}</div>
                                </>
                            )}
                            <div className="absolute bottom-3 left-3 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center"><ZoomIn className="w-3.5 h-3.5" /></div>
                        </div>
                        {/* Thumbs */}
                        {imagenes.length > 1 && (
                            <div className="flex gap-2 overflow-x-auto">
                                {imagenes.map((img, i) => (
                                    <button key={i} onClick={() => setImgIdx(i)}
                                        className="shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors"
                                        style={{ borderColor: i === imgIdx ? t.colors.accent : t.colors.border }}
                                        data-testid={`thumb-${i}`}>
                                        {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ background: t.colors.surface }} />}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Info */}
                    <div className="flex flex-col">
                        <p className="text-xs uppercase tracking-widest mb-1" style={{ color: t.colors.textMuted }}>{producto.marca || 'Karry Jean\'s'} {producto.modelo ? `• ${producto.modelo}` : ''}</p>
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <h1 className="text-2xl sm:text-3xl font-bold flex-1">{producto.nombre}</h1>
                            <button
                                onClick={toggleFavorito}
                                className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all active:scale-95"
                                style={{ background: isFav ? '#EC4899' : t.colors.surface, border: `1px solid ${isFav ? '#EC4899' : t.colors.border}` }}
                                title={isFav ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                                data-testid="btn-fav-detalle"
                            >
                                <Heart className={`w-5 h-5 ${isFav ? 'fill-white text-white' : ''}`} style={{ color: isFav ? '#fff' : t.colors.textMuted }} />
                            </button>
                        </div>
                        {producto.color && <p className="text-sm mb-3" style={{ color: t.colors.textMuted }}>Color: <span className="font-bold capitalize" style={{ color: t.colors.text }}>{producto.color}</span></p>}

                        {/* Precio */}
                        <div className="mb-4">
                            <p className="text-3xl font-extrabold" style={{ color: t.colors.accent }}>${precioActual.toLocaleString('es-MX', { minimumFractionDigits: 2 })}<span className="text-sm font-normal ml-2" style={{ color: t.colors.textMuted }}>MXN</span></p>
                            <p className="text-xs mt-1" style={{ color: t.colors.textMuted }}>{labelTipo(tipoCompra)}</p>
                        </div>

                        {/* Tipo de compra */}
                        <div className="mb-4">
                            <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: t.colors.textMuted }}>Tipo de compra</p>
                            {(() => {
                                const visibles = [showPieza, showPaquete, showCorrida].filter(Boolean).length;
                                if (visibles === 0) {
                                    return (
                                        <div className="p-3 rounded-lg border text-xs" style={{ borderColor: t.colors.border, color: t.colors.textMuted, background: t.colors.surface }}>
                                            No hay opciones de compra disponibles para este producto.
                                        </div>
                                    );
                                }
                                const gridCols = visibles === 1 ? 'grid-cols-1' : visibles === 2 ? 'grid-cols-2' : 'grid-cols-3';
                                return (
                                    <div className={`grid ${gridCols} gap-2`}>
                                        {showPieza && (
                                            <button onClick={() => setTipoCompra('pieza')}
                                                className="px-3 py-3 rounded-lg text-xs font-bold text-center transition-colors"
                                                style={{
                                                    background: tipoCompra === 'pieza' ? t.colors.accent : t.colors.surface,
                                                    color: tipoCompra === 'pieza' ? t.colors.badgeText : t.colors.text,
                                                    border: `1px solid ${tipoCompra === 'pieza' ? t.colors.accent : t.colors.border}`
                                                }}
                                                data-testid="tipo-pieza">
                                                Pieza<br /><span className="text-[10px] font-normal opacity-80">${producto.precio?.toLocaleString('es-MX')}</span>
                                            </button>
                                        )}
                                        {showPaquete && (
                                            <button onClick={() => setTipoCompra('paquete')}
                                                className="px-3 py-3 rounded-lg text-xs font-bold text-center transition-colors"
                                                style={{
                                                    background: tipoCompra === 'paquete' ? t.colors.accent : t.colors.surface,
                                                    color: tipoCompra === 'paquete' ? t.colors.badgeText : t.colors.text,
                                                    border: `1px solid ${tipoCompra === 'paquete' ? t.colors.accent : t.colors.border}`
                                                }}
                                                data-testid="tipo-paquete">
                                                Paquete<br /><span className="text-[10px] font-normal opacity-80">{`${producto.tamano_paquete || 14} pzas · $${producto.precio_paquete?.toLocaleString('es-MX')}`}</span>
                                            </button>
                                        )}
                                        {showCorrida && (
                                            <button onClick={() => setTipoCompra('corrida')}
                                                className="px-3 py-3 rounded-lg text-xs font-bold text-center transition-colors"
                                                style={{
                                                    background: tipoCompra === 'corrida' ? t.colors.accent : t.colors.surface,
                                                    color: tipoCompra === 'corrida' ? t.colors.badgeText : t.colors.text,
                                                    border: `1px solid ${tipoCompra === 'corrida' ? t.colors.accent : t.colors.border}`
                                                }}
                                                data-testid="tipo-corrida">
                                                Corrida<br /><span className="text-[10px] font-normal opacity-80">{(() => {
                                                    const tamano = producto.tamano_paquete || 14;
                                                    const corridaPzs = Math.ceil(tamano / 2);
                                                    const precio = (producto.precio_corrida || 0) > 0
                                                        ? producto.precio_corrida
                                                        : Math.round(((producto.precio_paquete || 0) / (tamano || 1) + 10) * corridaPzs * 100) / 100;
                                                    return `${corridaPzs} pzas · $${precio.toLocaleString('es-MX')}`;
                                                })()}</span>
                                            </button>
                                        )}
                                    </div>
                                );
                            })()}
                            <p className="text-[11px] mt-2 italic" style={{ color: t.colors.textMuted }}>
                                {tipoCompra === 'paquete' && 'Los paquetes ya estan armados con el mismo color y modelo. No se pueden mezclar.'}
                                {tipoCompra === 'corrida' && `La corrida incluye ${Math.ceil((producto?.tamano_paquete || 14) / 2)} piezas (mitad del paquete) del mismo modelo y color.`}
                                {tipoCompra === 'pieza' && 'Compra individual. Elige la talla que necesites.'}
                            </p>
                        </div>

                        {/* Tallas (solo para pieza) */}
                        {tipoCompra === 'pieza' && (producto.tallas_disponibles?.length > 0) && (
                            <div className="mb-4">
                                <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: t.colors.textMuted }}>Talla</p>
                                <div className="flex flex-wrap gap-2">
                                    {producto.tallas_disponibles.map(talla => (
                                        <button key={talla} onClick={() => setTallaSel(talla)}
                                            className="min-w-[44px] px-3 h-10 rounded-lg text-sm font-bold transition-colors"
                                            style={{
                                                background: tallaSel === talla ? t.colors.accent : t.colors.surface,
                                                color: tallaSel === talla ? t.colors.badgeText : t.colors.text,
                                                border: `1px solid ${tallaSel === talla ? t.colors.accent : t.colors.border}`
                                            }}
                                            data-testid={`talla-${talla}`}>
                                            {talla}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Cantidad */}
                        <div className="mb-5">
                            <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: t.colors.textMuted }}>Cantidad</p>
                            <div className="flex items-center gap-3">
                                <button onClick={() => setCantidad(Math.max(1, cantidad - 1))}
                                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                                    style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}` }}
                                    data-testid="btn-minus">
                                    <Minus className="w-4 h-4" />
                                </button>
                                <span className="text-lg font-bold w-12 text-center" data-testid="cantidad-valor">{cantidad}</span>
                                <button onClick={() => setCantidad(cantidad + 1)}
                                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                                    style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}` }}
                                    data-testid="btn-plus">
                                    <Plus className="w-4 h-4" />
                                </button>
                                <span className="ml-auto text-sm" style={{ color: t.colors.textMuted }}>
                                    Subtotal: <span className="font-bold text-base" style={{ color: t.colors.text }}>${subtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                                </span>
                            </div>
                        </div>

                        {/* Stock */}
                        {typeof producto.stock === 'number' && producto.stock > 0 && (
                            <p className="text-xs mb-3 flex items-center gap-1.5" style={{ color: '#10B981' }}>
                                <Check className="w-3 h-3" /> Disponible ({producto.stock} en stock)
                            </p>
                        )}

                        {/* CTAs */}
                        {added && (
                            <div className="mb-3 px-4 py-3 rounded-lg flex items-center justify-between gap-2" style={{ background: '#10B98120', border: '1px solid #10B981' }}>
                                <div className="flex items-center gap-2 text-emerald-500 text-sm font-bold">
                                    <Check className="w-4 h-4" /> Agregado al carrito
                                </div>
                                <Link to="/tienda?cart=1" className="px-3 py-1.5 rounded text-xs font-bold bg-emerald-500 text-white hover:bg-emerald-600" data-testid="link-ver-carrito">
                                    Ver carrito ({cartCount}) →
                                </Link>
                            </div>
                        )}
                        <div className="flex flex-col sm:flex-row gap-2 mb-3">
                            <button onClick={addToCart}
                                className="flex-1 px-5 py-3.5 rounded-lg font-bold text-sm transition-opacity hover:opacity-90 flex items-center justify-center gap-2 shadow-lg"
                                style={{ background: t.colors.accent, color: t.colors.badgeText }}
                                data-testid="btn-add-cart">
                                {added ? <><Check className="w-4 h-4" /> Agregado</> : <><ShoppingBag className="w-4 h-4" /> Agregar al carrito</>}
                            </button>
                        </div>
                        <button onClick={cotizarWA}
                            className="px-5 py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                            style={{ background: '#25D366', color: '#fff' }}
                            data-testid="btn-cotizar-producto">
                            <MessageCircle className="w-4 h-4" /> Cotizar por WhatsApp
                        </button>

                        {/* Descripcion */}
                        {producto.descripcion && (
                            <div className="mt-6 pt-5" style={{ borderTop: `1px solid ${t.colors.border}` }}>
                                <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: t.colors.textMuted }}>Descripcion</p>
                                <p className="text-sm whitespace-pre-line" style={{ color: t.colors.textMuted }}>{producto.descripcion}</p>
                            </div>
                        )}

                        {/* Info adicional */}
                        <div className="mt-4 grid grid-cols-2 gap-3 text-xs" style={{ color: t.colors.textMuted }}>
                            {producto.categoria && <div><span className="font-bold" style={{ color: t.colors.text }}>Categoria:</span> {producto.categoria}</div>}
                            {producto.modelo && <div><span className="font-bold" style={{ color: t.colors.text }}>Modelo:</span> {producto.modelo}</div>}
                            {producto.segmento && <div><span className="font-bold" style={{ color: t.colors.text }}>Segmento:</span> <span className="capitalize">{producto.segmento}</span></div>}
                        </div>
                    </div>
                </div>

                {/* ==================== MAS DE ESTA MARCA ==================== */}
                {masDeMarca.length > 0 && (
                    <section className="mt-12 pt-8 border-t" style={{ borderColor: t.colors.border }}>
                        <div className="flex items-baseline justify-between mb-5">
                            <div>
                                <h2 className="text-lg sm:text-2xl font-bold uppercase tracking-wider" style={{ fontFamily: t.font }}>
                                    Mas de {producto.marca || 'esta marca'}
                                </h2>
                                <p className="text-xs mt-1" style={{ color: t.colors.textMuted }}>{masDeMarca.length} modelos disponibles</p>
                            </div>
                            <Link to={`/tienda?marca=${encodeURIComponent(producto.marca || '')}`} className="text-xs font-bold underline" style={{ color: t.colors.accent }}>
                                Ver todos
                            </Link>
                        </div>
                        <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide -mx-3 px-3 snap-x" data-testid="seccion-mas-de-marca">
                            {masDeMarca.map(p => (
                                <Link
                                    key={p.id}
                                    to={`/producto/${p.id}`}
                                    className="shrink-0 w-40 sm:w-48 snap-start group"
                                    data-testid={`mas-marca-${p.id}`}
                                >
                                    <div className="aspect-[3/4] mb-2 overflow-hidden rounded" style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}` }}>
                                        {p.imagenes?.[0]
                                            ? <img src={p.imagenes[0]} alt={p.nombre} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                            : <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="w-8 h-8" style={{ color: t.colors.border }} /></div>}
                                    </div>
                                    <p className="text-[10px] uppercase tracking-wider" style={{ color: t.colors.textMuted }}>{p.marca}</p>
                                    <p className="text-xs font-medium line-clamp-2">{p.nombre}</p>
                                    <p className="text-sm font-bold mt-0.5" style={{ color: t.colors.accent }}>${p.precio?.toLocaleString('es-MX')}</p>
                                    {(p.precio_paquete > 0 || p.precio_corrida > 0) && (
                                        <p className="text-[9px]" style={{ color: t.colors.textMuted }}>
                                            {p.precio_paquete > 0 && `Paq $${p.precio_paquete.toLocaleString('es-MX')}`}
                                            {p.precio_paquete > 0 && p.precio_corrida > 0 && ' · '}
                                            {p.precio_corrida > 0 && `Cor $${p.precio_corrida.toLocaleString('es-MX')}`}
                                        </p>
                                    )}
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* ==================== TAMBIEN TE PODRIA INTERESAR ==================== */}
                {relacionados.length > 0 && (
                    <section className="mt-12 pt-8 border-t" style={{ borderColor: t.colors.border }}>
                        <div className="mb-5">
                            <h2 className="text-lg sm:text-2xl font-bold uppercase tracking-wider" style={{ fontFamily: t.font }}>
                                Tambien te podria interesar
                            </h2>
                            <p className="text-xs mt-1" style={{ color: t.colors.textMuted }}>Productos relacionados</p>
                        </div>
                        <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide -mx-3 px-3 snap-x" data-testid="seccion-relacionados">
                            {relacionados.map(p => (
                                <Link
                                    key={p.id}
                                    to={`/producto/${p.id}`}
                                    className="shrink-0 w-40 sm:w-48 snap-start group"
                                    data-testid={`relacionado-${p.id}`}
                                >
                                    <div className="aspect-[3/4] mb-2 overflow-hidden rounded" style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}` }}>
                                        {p.imagenes?.[0]
                                            ? <img src={p.imagenes[0]} alt={p.nombre} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                            : <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="w-8 h-8" style={{ color: t.colors.border }} /></div>}
                                    </div>
                                    <p className="text-[10px] uppercase tracking-wider" style={{ color: t.colors.textMuted }}>{p.marca || 'Karry Jean\'s'}</p>
                                    <p className="text-xs font-medium line-clamp-2">{p.nombre}</p>
                                    <p className="text-sm font-bold mt-0.5" style={{ color: t.colors.accent }}>${p.precio?.toLocaleString('es-MX')}</p>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </div>

            {/* Modal zoom */}
            {zoom && imagenes[imgIdx] && (
                <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4" onClick={() => setZoom(false)} data-testid="zoom-modal">
                    <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center" onClick={() => setZoom(false)}>
                        <X className="w-5 h-5" />
                    </button>
                    <img src={imagenes[imgIdx]} alt="" className="max-w-full max-h-full object-contain" />
                </div>
            )}
        </div>
    );
};

export default ProductoDetalle;
